import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter } from '@angular/material/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getStorage, provideStorage } from '@angular/fire/storage';

import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { routes } from './app.routes';

import { registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';

import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  SciGuaduaModule,
  SciGuaduaAuthenticationInterceptor,
  SciGuaduaAuthorizationInterceptor,
} from '@scifamek/guadua';
import { environment } from '../environments/environment';
import { ROOT_REDUCERS } from './app.state';
import { authPersistenceMetaReducer } from './features/auth/state-management/auth-persistence.meta-reducer';
import { codeExtractor } from './core/constants';
import { Home } from './core/views/home/home';
import { Login } from './core/views/login/login';
import { I18N_INITIALIZER } from './core/i18n';
import { SESSION_INITIALIZER } from './core/initializers/session.initializer';
import {
  selectAuthenticationToken,
  selectAuthorizationToken,
  selectAvailableResources,
} from './features/auth/state-management/auth.selectors';
import { ApartmentEffects } from './features/apartment/state-management/apartment.effects';
import { AuthEffects } from './features/auth/state-management/auth.effects';
import { BookingEffects } from './features/booking/state-management/booking.effects';
import { BuildingEffects } from './features/building/state-management/building.effects';
import { CategoryEffects } from './features/category/state-management/category.effects';
import { DoormanEffects } from './features/doorman/state-management/doorman.effects';
import { PackageEffects } from './features/package/state-management/package.effects';
import { PermanenceBuildingEffects } from './features/permanence-building/state-management/permanence-building.effects';
import { ResidentEffects } from './features/resident/state-management/resident.effects';
import { SubcategoryEffects } from './features/subcategory/state-management/subcategory.effects';
import { VendorEffects } from './features/vendor/state-management/vendor.effects';
import { VendorPaymentEffects } from './features/vendor-payment/state-management/vendor-payment.effects';
import { VendorRateEffects } from './features/vendor-rate/state-management/vendor-rate.effects';
import { MembershipEffects } from './features/membership/state-management/membership.effects';
import { PackagePurchaseEffects } from './features/package-purchase/state-management/package-purchase.effects';
import { BaseEffects } from './state-management/base.effects';
import { methodMapper } from '@resident-select/domain/core';
import { VendorCompanyEffects } from './features/vendor-company/state-management/vendor-company.effects';
import { BuildingAreaEffects } from './features/building-area/state-management/building-area.effects';
import { AccessPassEffects } from './features/access-pass/state-management/access-pass.effects';
import { BookingPassEffects } from './features/booking-pass/state-management/booking-pass.effects';
import { AgencyEffects } from './features/agency/state-management/agency.effects';
import { AgencyAdminEffects } from './features/agency-admin/state-management/agency-admin.effects';
import { AgencyUserEffects } from './features/agency-user/state-management/agency-user.effects';
import { AgencyApartmentAssignmentEffects } from './features/agency-apartment-assignment/state-management/agency-apartment-assignment.effects';
import { DynamicPermissionEffects } from './features/dynamic-permission/state-management/dynamic-permission.effects';

registerLocaleData(localeEs);

const translate = TranslateModule.forRoot({
  defaultLanguage: 'es',
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient],
  },
});

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    I18N_INITIALIZER,
    SESSION_INITIALIZER,
    TranslateService,
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    importProvidersFrom(translate),
    ...(typeof environment.firebaseConfig === 'object' && environment.firebaseConfig !== null
      ? [
          provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
          provideAuth(() => getAuth()),
          provideStorage(() => getStorage()),
        ]
      : []),
    provideStore(ROOT_REDUCERS, { metaReducers: [authPersistenceMetaReducer] }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
    }),
    provideEffects(
      AccessPassEffects,
      AgencyEffects,
      AgencyAdminEffects,
      AgencyApartmentAssignmentEffects,
      AgencyUserEffects,
      ApartmentEffects,
      AuthEffects,
      BaseEffects,
      BookingEffects,
      BookingPassEffects,
      BuildingEffects,
      BuildingAreaEffects,
      CategoryEffects,
      DoormanEffects,
      DynamicPermissionEffects,
      MembershipEffects,
      PackageEffects,
      PackagePurchaseEffects,
      PermanenceBuildingEffects,
      ResidentEffects,
      SubcategoryEffects,
      VendorEffects,
      VendorPaymentEffects,
      VendorRateEffects,
      VendorCompanyEffects,
    ),

    importProvidersFrom(
      SciGuaduaModule.forRoot({
        selectors: {
          selectAuthenticationToken,
          selectAuthorizationToken,
          selectAvailableResources,
        },
        translate: {
          prefix: 'events',
        },
        routing: {
          homeRoute: Home.route,
          loginRoute: Login.route,
        },
        server: {
          methodsMapper: methodMapper!,
          serverPath: environment.server,
          codeExtractor,
        },
        modal: {
          notAllowedMessageConfiguration: {
            title: 'No tienes permitido acceder a esta funcionalidad',
          },
        },
      }),
    ),

    provideClientHydration(withEventReplay()),
    provideHttpClient(
      withInterceptors([SciGuaduaAuthenticationInterceptor, SciGuaduaAuthorizationInterceptor]),
    ),
  ],
};
